import { useCallback, useEffect, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';

interface BrowseEntry {
    name: string;
    path: string;
    type: 'dir' | 'file';
}
interface BrowseResponse {
    owner: string;
    name: string;
    defaultBranch: string;
    path: string;
    entries: BrowseEntry[];
    registered: boolean;
}

interface Props {
    repoId: string;                                 // owner/repo (lowercase)
    fullName: string;                               // owner/repo with canonical casing for display
    initialIncludes?: string[];
    initialExcludes?: string[];
    /** Optional override for the modal title. Defaults to indexing-flow phrasing. */
    title?: string;
    /** Optional override for the explanatory hint above the tree. */
    helperText?: React.ReactNode;
    /** When true, show ONLY paths that are actually indexed in this repo
     *  (queried from the documents container via the repo-tree endpoint).
     *  Used for role-override picking, where any path outside the repo's
     *  indexed set would produce a dead grant. Default false keeps the
     *  legacy whole-GitHub-tree behavior for the indexing-setup flow,
     *  where you have to be able to pick paths that aren't yet indexed. */
    indexedOnly?: boolean;
    onClose: () => void;
    onConfirm: (sel: { includes: string[]; excludes: string[] }) => void;
}

type Decision = 'include' | 'exclude' | null;

interface IndexedTreeResponse {
    files: Array<{ path: string }>;
    truncated?: boolean;
}

/**
 * Build a folder hierarchy from a flat list of indexed file paths.
 * Returns a map of `parentPath` → list of immediate children, matching
 * the shape `loadDir()` populates. Leaf files become `type: 'file'`;
 * intermediate segments become `type: 'dir'`. Sort matches the existing
 * pattern (dirs first, then files, each alphabetical).
 */
function buildIndexedTree(paths: string[]): Record<string, BrowseEntry[]> {
    type ChildInfo = { type: 'dir' | 'file'; path: string };
    const byParent = new Map<string, Map<string, ChildInfo>>();

    for (const fullPath of paths) {
        if (!fullPath) continue;
        const segments = fullPath.split('/').filter(Boolean);
        let parent = '';
        for (let i = 0; i < segments.length; i++) {
            const name = segments[i];
            const path = segments.slice(0, i + 1).join('/');
            const isLeaf = i === segments.length - 1;
            const type: 'dir' | 'file' = isLeaf ? 'file' : 'dir';
            if (!byParent.has(parent)) byParent.set(parent, new Map());
            const siblings = byParent.get(parent)!;
            const existing = siblings.get(name);
            if (!existing) {
                siblings.set(name, { type, path });
            } else if (existing.type === 'file' && type === 'dir') {
                // Path appears as both a file and a parent — shouldn't
                // happen with indexed-content paths, but be safe:
                // a parent of other files cannot ALSO be a leaf file.
                siblings.set(name, { type: 'dir', path });
            }
            parent = path;
        }
    }

    const result: Record<string, BrowseEntry[]> = {};
    for (const [parent, siblings] of byParent) {
        result[parent] = Array.from(siblings.entries())
            .map(([name, info]) => ({ name, path: info.path, type: info.type }))
            .sort((a, b) => {
                if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
                return a.name.localeCompare(b.name);
            });
    }
    // Ensure the root key always exists, even on an empty indexed set.
    if (!result['']) result[''] = [];
    return result;
}

export function FolderPickerModal({ repoId, fullName, initialIncludes, initialExcludes, title, helperText, indexedOnly, onClose, onConfirm }: Props) {
    const [open, setOpen] = useState<Set<string>>(new Set(['']));
    const [children, setChildren] = useState<Record<string, BrowseEntry[]>>({});
    const [loadingPath, setLoadingPath] = useState<string | null>('');
    const [errorByPath, setErrorByPath] = useState<Record<string, string>>({});
    const [indexedTruncated, setIndexedTruncated] = useState(false);
    const [includes, setIncludes] = useState<Set<string>>(new Set(initialIncludes ?? []));
    const [excludes, setExcludes] = useState<Set<string>>(new Set(initialExcludes ?? []));

    const loadDir = useCallback(async (path: string) => {
        if (indexedOnly) return; // children populated up-front in indexed-only mode
        if (children[path] !== undefined) return;
        setLoadingPath(path);
        setErrorByPath((e) => { const n = { ...e }; delete n[path]; return n; });
        try {
            const [owner, name] = repoId.split('/');
            const url = `/manage/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/browse${path ? `?path=${encodeURIComponent(path)}` : ''}`;
            const r = await apiCall<BrowseResponse>(url);
            setChildren((c) => ({ ...c, [path]: r.entries }));
        } catch (err) {
            setErrorByPath((e) => ({ ...e, [path]: err instanceof ApiError ? err.message : (err as Error).message }));
        } finally {
            setLoadingPath((p) => (p === path ? null : p));
        }
    }, [children, repoId, indexedOnly]);

    useEffect(() => {
        if (indexedOnly) return;
        void loadDir('');
    }, [indexedOnly, loadDir]);

    // Indexed-only mode: bulk-fetch the repo's indexed paths once and
    // build the whole client-side tree. Skips per-directory GitHub calls
    // entirely — admins see only what's actually addressable.
    useEffect(() => {
        if (!indexedOnly) return;
        let cancelled = false;
        setLoadingPath('');
        setErrorByPath({});
        setIndexedTruncated(false);
        (async () => {
            try {
                const [owner, name] = repoId.split('/');
                const url = `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/tree?limit=5000`;
                const r = await apiCall<IndexedTreeResponse>(url);
                if (cancelled) return;
                setChildren(buildIndexedTree(r.files.map((f) => f.path)));
                setIndexedTruncated(!!r.truncated);
                setLoadingPath(null);
            } catch (err) {
                if (cancelled) return;
                setErrorByPath({ '': err instanceof ApiError ? err.message : (err as Error).message });
                setLoadingPath(null);
            }
        })();
        return () => { cancelled = true; };
    }, [indexedOnly, repoId]);

    function toggleOpen(path: string) {
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(path)) { next.delete(path); return next; }
            next.add(path);
            return next;
        });
        if (!children[path]) void loadDir(path);
    }

    function decisionFor(path: string): Decision {
        if (includes.has(path)) return 'include';
        if (excludes.has(path)) return 'exclude';
        return null;
    }
    function ancestorIncluded(path: string): boolean {
        if (includes.size === 0) return true; // empty includes = whole repo by default
        for (const inc of includes) {
            if (path === inc || path.startsWith(inc + '/')) return true;
        }
        return false;
    }
    function ancestorExcluded(path: string): boolean {
        for (const ex of excludes) {
            if (path === ex || path.startsWith(ex + '/')) return true;
        }
        return false;
    }

    function setInclude(path: string) {
        setIncludes((s) => {
            const next = new Set(s);
            // Remove any descendants — picking a parent supersedes children
            for (const p of Array.from(next)) {
                if (p === path || p.startsWith(path + '/')) next.delete(p);
            }
            next.add(path);
            return next;
        });
        setExcludes((s) => {
            const next = new Set(s);
            next.delete(path);
            return next;
        });
    }
    function setExclude(path: string) {
        setExcludes((s) => {
            const next = new Set(s);
            for (const p of Array.from(next)) {
                if (p === path || p.startsWith(path + '/')) next.delete(p);
            }
            next.add(path);
            return next;
        });
        setIncludes((s) => {
            const next = new Set(s);
            next.delete(path);
            return next;
        });
    }
    function clearDecision(path: string) {
        setIncludes((s) => { const n = new Set(s); n.delete(path); return n; });
        setExcludes((s) => { const n = new Set(s); n.delete(path); return n; });
    }

    function effectiveState(path: string): 'in' | 'out' | 'default' {
        const own = decisionFor(path);
        if (own === 'include') return 'in';
        if (own === 'exclude') return 'out';
        if (ancestorExcluded(path)) return 'out';
        if (ancestorIncluded(path)) return 'in';
        return 'default';
    }

    function renderEntry(entry: BrowseEntry, depth: number) {
        const isOpen = open.has(entry.path);
        const decision = decisionFor(entry.path);
        const eff = effectiveState(entry.path);
        const indent = depth * 18 + 8;
        if (entry.type === 'file') {
            return (
                <li key={entry.path}>
                    <div className="picker-row picker-row--file">
                        <span style={{ paddingLeft: indent + 22 }} className={'picker-status picker-status--' + eff}>
                            {entry.name}
                        </span>
                        <div className="picker-actions">
                            <button
                                type="button"
                                className={'picker-btn' + (decision === 'include' ? ' picker-btn--active picker-btn--in' : '')}
                                onClick={() => decision === 'include' ? clearDecision(entry.path) : setInclude(entry.path)}
                                title="Include just this file"
                            >
                                ✓ Include
                            </button>
                            <button
                                type="button"
                                className={'picker-btn' + (decision === 'exclude' ? ' picker-btn--active picker-btn--out' : '')}
                                onClick={() => decision === 'exclude' ? clearDecision(entry.path) : setExclude(entry.path)}
                                title="Exclude this file"
                            >
                                ✗ Exclude
                            </button>
                        </div>
                    </div>
                </li>
            );
        }
        return (
            <li key={entry.path}>
                <div className="picker-row">
                    <button type="button" className="picker-caret" onClick={() => toggleOpen(entry.path)} style={{ marginLeft: indent }} aria-expanded={isOpen}>
                        {isOpen ? '▾' : '▸'}
                    </button>
                    <span className={'picker-status picker-status--' + eff} title={`Effective: ${eff}`}>
                        {entry.name}
                    </span>
                    <div className="picker-actions">
                        <button
                            type="button"
                            className={'picker-btn' + (decision === 'include' ? ' picker-btn--active picker-btn--in' : '')}
                            onClick={() => decision === 'include' ? clearDecision(entry.path) : setInclude(entry.path)}
                            title="Include this folder (everything under it)"
                        >
                            ✓ Include
                        </button>
                        <button
                            type="button"
                            className={'picker-btn' + (decision === 'exclude' ? ' picker-btn--active picker-btn--out' : '')}
                            onClick={() => decision === 'exclude' ? clearDecision(entry.path) : setExclude(entry.path)}
                            title="Exclude this folder (everything under it)"
                        >
                            ✗ Exclude
                        </button>
                    </div>
                </div>
                {isOpen && (
                    <ul className="picker-list">
                        {loadingPath === entry.path && <li className="picker-loading" style={{ paddingLeft: indent + 24 }}>Loading…</li>}
                        {errorByPath[entry.path] && <li className="picker-error" style={{ paddingLeft: indent + 24 }}>{errorByPath[entry.path]}</li>}
                        {children[entry.path]?.map((c) => renderEntry(c, depth + 1))}
                    </ul>
                )}
            </li>
        );
    }

    const rootEntries = children[''] ?? [];

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="modal-header">
                    <h2>{title ?? `Choose folders to index — ${fullName}`}</h2>
                    <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
                </header>
                <div className="modal-body">
                    <p className="form-hint">
                        {helperText ?? (
                            <>
                                Click <strong>Include</strong> on folders you want indexed (or leave everything blank
                                for the whole repo). Click <strong>Exclude</strong> to omit subfolders. Picking a
                                folder applies to everything under it.
                            </>
                        )}
                    </p>
                    <div className="picker-summary">
                        <strong>Include:</strong>{' '}
                        {includes.size === 0 ? <em>whole repo</em> : Array.from(includes).map((p) => <code key={p}>{p}</code>).reduce((acc, el, i) => i === 0 ? [el] : [...acc, ' ', el], [] as React.ReactNode[])}
                        <br />
                        <strong>Exclude:</strong>{' '}
                        {excludes.size === 0 ? <em>none</em> : Array.from(excludes).map((p) => <code key={p}>{p}</code>).reduce((acc, el, i) => i === 0 ? [el] : [...acc, ' ', el], [] as React.ReactNode[])}
                    </div>
                    {errorByPath[''] && <div className="form-error">{errorByPath['']}</div>}
                    {indexedOnly && indexedTruncated && (
                        <div className="form-hint">
                            <em>
                                Showing the first 5000 indexed paths in this repo. Paths beyond that limit aren't
                                visible here and can't be selected — narrow the repo's indexing scope in
                                Admin → Repos (e.g. <code>indexPaths</code>) so the relevant subtree fits within
                                the limit.
                            </em>
                        </div>
                    )}
                    {indexedOnly && !errorByPath[''] && loadingPath === null && rootEntries.length === 0 && (
                        <div className="admin-page__empty">
                            No indexed paths in this repo yet. (Indexing may still be in progress, or the repo's
                            indexPaths / docTypeFilters may exclude everything.)
                        </div>
                    )}
                    {loadingPath === '' && rootEntries.length === 0 && <div className="picker-loading">Loading repo root…</div>}
                    <ul className="picker-list picker-list--root">
                        {rootEntries.map((c) => renderEntry(c, 0))}
                    </ul>
                </div>
                <footer className="modal-footer">
                    <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
                    <button
                        className="btn btn--primary"
                        onClick={() => onConfirm({ includes: Array.from(includes), excludes: Array.from(excludes) })}
                    >
                        Save selection
                    </button>
                </footer>
            </div>
        </div>
    );
}
